import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Paper } from "@mui/material";
import { useUserEvents } from "../../contexts/UserEvent";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/Auth";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventDialog from "./EventDialog";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface MainGridProps {
  onAddNewClick: () => void;
  onRefresh: () => void;
}

const MainGrid: React.FC<MainGridProps> = ({ onAddNewClick, onRefresh }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userEvents, deleteEvent, updateUrl } = useUserEvents();
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<any>(null);

  const buttonStyles = {
    contained: {
      background: "linear-gradient(45deg, #0A3671 30%, #11539E 90%)",
      borderRadius: "50px",
      padding: "12px 35px",
      textTransform: "none",
      fontSize: "1.1rem",
      fontWeight: 600,
      color: "white",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      boxShadow: "0 0 15px rgba(255, 255, 255, 0.2)",
      "&:hover": {
        background: "linear-gradient(45deg, #0A2A4A 30%, #041C2C 90%)",
        boxShadow: "0 0 20px rgba(255, 255, 255, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.3)",
      },
    },
  };

  useEffect(() => {
    onRefresh();
  }, [onRefresh]);

  const NoRowsOverlay = () => {
    return (
      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
        }}
      >
        You are not tracking any Events
      </Box>
    );
  };

  function handleDeleteClick(eventId: number) {
    setEventToDelete(eventId);
    setDeleteDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (eventToDelete !== null) {
      await deleteEvent(eventToDelete);
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  }

  function handleCancelDelete() {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  }

  function handleEditClick(event: any) {
    setEventToEdit(event);
    setEditDialogOpen(true);
  }

  function handleEditDialogClose() {
    setEditDialogOpen(false);
    setEventToEdit(null);
  }

  function handleEditDialogSuccess() {
    setEditDialogOpen(false);
    setEventToEdit(null);
    onRefresh();
  }

  function EventRow({ event, isLast }: { event: any; isLast: boolean }) {
    const isOpen = !!openRows[event.id];
    const trackedUrl =
      event.trackedUrls && event.trackedUrls.length > 0
        ? event.trackedUrls[0]
        : null;
    const handleRefreshTracking = async () => {
      if (trackedUrl) {
        await updateUrl(trackedUrl.url, { keepTracking: true });
      }
    };
    return (
      <>
        <TableRow
          sx={{
            background: "rgba(30, 58, 138, 0.07)",
            boxShadow: isOpen
              ? "0 2px 8px 0 rgba(59,130,246,0.10)"
              : "0 1px 4px 0 rgba(59,130,246,0.07)",
            borderRadius: "12px",
            "&:last-of-type": {
              borderBottom: 0,
            },
            transition: "box-shadow 0.2s",
          }}
        >
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() =>
                setOpenRows((prev) => ({
                  ...prev,
                  [event.id]: !prev[event.id],
                }))
              }
            >
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {event.name}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>
            {trackedUrl ? trackedUrl.maxPrice : "-"}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>
            {trackedUrl ? trackedUrl.quantity : "-"}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>
            {event.lowestPrice !== undefined &&
            event.lowestPrice !== null &&
            event.lowestPrice !== ""
              ? event.lowestPrice
              : "-"}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>
            {trackedUrl ? (
              <Chip
                label={trackedUrl.notificationSent ? "Yes" : "No"}
                color={trackedUrl.notificationSent ? "success" : "default"}
                size="small"
                variant={trackedUrl.notificationSent ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  minWidth: 40,
                  fontSize: "0.95em",
                }}
              />
            ) : (
              "-"
            )}
          </TableCell>
          <TableCell sx={{ textAlign: "center" }}>
            {trackedUrl ? (
              <Chip
                label={trackedUrl.keepTracking ? "Yes" : "No"}
                color={trackedUrl.keepTracking ? "success" : "default"}
                size="small"
                variant={trackedUrl.keepTracking ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  minWidth: 40,
                  fontSize: "0.95em",
                }}
              />
            ) : (
              "-"
            )}
          </TableCell>
          <TableCell
            sx={{
              textAlign: "center",
              p: 0,
              minWidth: 80,
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.5 },
                pr: 1,
              }}
            >
              {trackedUrl && !trackedUrl.keepTracking && (
                <IconButton
                  aria-label="refresh tracking"
                  size="small"
                  color="primary"
                  onClick={handleRefreshTracking}
                >
                  <RefreshIcon />
                </IconButton>
              )}
              <IconButton
                aria-label="edit event"
                size="small"
                color="primary"
                onClick={() => handleEditClick(event)}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                aria-label="delete event"
                size="small"
                color="error"
                onClick={() => handleDeleteClick(event.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse
              in={isOpen}
              timeout="auto"
              unmountOnExit
              sx={{ width: "95%", alignContent: "center", margin: "0 auto" }}
            >
              <Box margin={1}>
                <Table size="small" aria-label="tracked-urls">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& .MuiTableCell-head": {
                          color: "white",
                          fontWeight: 700,
                          fontSize: "1.02rem",
                          letterSpacing: "0.02em",
                          borderBottom: "2px solid rgba(247, 247, 247, 0.08)", // lighter, dashed border for clear distinction
                        },
                      }}
                    >
                      <TableCell sx={{ width: "70%" }}>URL</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        Section Filter
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {event.trackedUrls && event.trackedUrls.length > 0 ? (
                      event.trackedUrls.map((url: any, idx: number) => (
                        <TableRow
                          key={idx}
                          sx={
                            idx === 0
                              ? { "& > td": { paddingTop: "12px" } }
                              : {}
                          }
                        >
                          <TableCell>
                            <Link href={url.url} target="_blank" rel="noopener">
                              {url.url}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            {url.sectionFilter && url.sectionFilter.length > 0
                              ? url.sectionFilter.join(", ")
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ textAlign: "center" }}>
                          No tracked URLs
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
        {!isLast && (
          <TableRow>
            <TableCell colSpan={7} sx={{ p: 0, background: "transparent" }}>
              <Box
                sx={{
                  mx: 2,
                  my: 1.5,
                  borderBottom: "2px solid rgba(59,130,246,0.3)",
                  boxShadow: "0 1px 0 0 rgba(59,130,246,0.06)",
                  borderRadius: "1px",
                }}
              />
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <EventDialog
        open={editDialogOpen}
        mode="edit"
        initialEvent={eventToEdit}
        onClose={handleEditDialogClose}
        onSuccess={handleEditDialogSuccess}
      />
      <Typography
        variant="h6"
        sx={{
          width: "100%",
          textAlign: "center",
          fontSize: { xs: "1.5rem", md: "2.125rem" },
        }}
      >
        You will be notified via email if tickets match your price criteria
      </Typography>
      <Box
        sx={{
          padding: { xs: "1rem", md: "2rem" },
        }}
      >
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow
                sx={{
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                    fontSize: "1.05rem",
                    letterSpacing: "0.03em",
                    borderBottom: "2px solid #3B82F6",
                  },
                }}
              >
                <TableCell sx={{ whiteSpace: "nowrap" }} />
                <TableCell sx={{ width: "30%", whiteSpace: "nowrap" }}>
                  Event Name
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    Price Limit
                    <Tooltip title="Per Ticket" arrow>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          ml: 0.5,
                          mt: -0.1,
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Quantity
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                    Found Price
                    <Tooltip title="Lowest Price Found - Per Ticket" arrow>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          ml: 0.5,
                          mt: -0.1,
                          verticalAlign: "middle",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Notification Sent
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Tracking
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userEvents && userEvents.length > 0 ? (
                userEvents.map((event: any, idx: number) => (
                  <EventRow
                    key={event.id}
                    event={event}
                    isLast={idx === userEvents.length - 1}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <NoRowsOverlay />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: -2 }}>
        <Button variant="contained" onClick={onAddNewClick}>
          Track New Event
        </Button>
        {user && !user.hasActiveSubscription && (
          <Button
            variant="contained"
            onClick={() => navigate("/plans")}
            sx={buttonStyles.contained}
          >
            Upgrade
          </Button>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            pt: 3,
            pb: 1,
            height: "100%",
            textAlign: "center",
            background: "rgba(17, 25, 44, 0.7)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 0 20px rgba(0, 0, 0, 0.3)",
            transition: "transform 0.3s ease-in-out",
            padding: { xs: "1rem", md: "2rem" },
            margin: { xs: "0 0.5rem", md: "0 1.5rem", lg: "0 2rem" },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              width: "100%",
              color: "transparent",
              backgroundImage:
                "linear-gradient(45deg, #3B82F6 30%, #60A5FA 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              textAlign: "left",
              mb: 2,
              fontSize: { xs: "1.4rem", md: "1.8rem" },
              fontWeight: 600,
            }}
          >
            Quick Start Guide
          </Typography>
          <Typography
            variant="h5"
            sx={{
              width: "100%",
              textAlign: "left",
              opacity: 0.9,
              mb: 3,
            }}
          >
            <Box
              component="ul"
              sx={{
                mt: 2,
                textAlign: "left",
                display: "inline-block",
                margin: "0 auto",
                fontSize: { xs: "0.9rem", md: "1.1rem" },
                lineHeight: 1.8,
                listStyleType: "none",
                marginLeft: { xs: "-2rem", md: "0" }, // Move list items to the left on smaller screens
                "& li": {
                  position: "relative",
                  paddingLeft: "1.5em",
                  "&::before": {
                    content: '"•"',
                    position: "absolute",
                    left: 0,
                    color: "#4A9EFF",
                    fontSize: "1.2em",
                    top: "-0.1em",
                  },
                },
              }}
            >
              <li>
                Click the <strong>Track New Event</strong> button to begin
              </li>
              <li>Give your event a name (e.g., "Taylor Swift Concert")</li>
              <li>
                Find your event on any supported platform (Ticketmaster,
                SeatGeek, StubHub, VividSeats or TickPick)
              </li>
              <li>Copy and paste the URL into the URL field</li>
              <li>Set your maximum per ticket price (with fees included)</li>
              <li>Specify how many tickets you need</li>
              <li>
                Hit <strong>Add</strong> to start tracking
              </li>
              <li>
                We'll email you when tickets matching your criteria become
                available
              </li>
              <li>Pro users can enable Whatsapp Alerts in the menu</li>
            </Box>
          </Typography>
        </Paper>
      </Box>
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this event? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainGrid;
